import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { Wand2, ArrowRight, Sparkles, Info, Camera, Image as ImageIcon, Trash2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../firebase/AuthContext';

export default function CustomCharacter() {
  const { language, isRTL } = useLanguage();
  const { user, getAuthToken } = useAuth();
  const [customPrompt, setCustomPrompt] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [images, setImages] = useState([]); // {id, name, dataUrl, role: 'background' | 'style'}
  const [isSaving, setIsSaving] = useState(false);

  const examples = language === 'he' ? [
    'שודד חלל עתידני עם שריון זוהר וכלי נשק לייזר',
    'קוסם בימי הביניים עם גלימה סגולה ומטה קסם',
    'גיבור על בסגנון קומיקס עם גלימה אדומה',
    'נסיך/נסיכה מאגדות עם שמלה מפוארת',
  ] : [
    'A futuristic space pirate with glowing armor and laser weapons',
    'A medieval wizard with a purple robe and magic staff',
    'A comic book style superhero with a red cape',
    'A fairy tale prince/princess with an elegant gown',
  ];

  const handleExample = (example) => {
    setCustomPrompt(example);
  };

  const handleFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImages((prev) => {
          const alreadyHasBackground = prev.some((img) => img.role === 'background');
          const role = alreadyHasBackground ? 'style' : 'background';
          return [
            ...prev,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
              name: file.name,
              dataUrl: reader.result,
              role,
            },
          ];
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const setAsBackground = (id) => {
    setImages((prev) =>
      prev.map((img) => ({
        ...img,
        role: img.id === id ? 'background' : img.role === 'background' ? 'style' : img.role,
      }))
    );
  };

  const removeImage = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleSaveCharacter = async () => {
    if (!customPrompt.trim()) {
      toast.error(
        language === 'he'
          ? 'צריך למלא תיאור דמות לפני שמירה'
          : 'Please enter a character description before saving'
      );
      return;
    }

    if (!user) {
      toast.error(language === 'he' ? 'יש להתחבר כדי לשמור דמות' : 'Please log in to save a character');
      return;
    }

    setIsSaving(true);
    try {
      const backgroundImage = images.find((img) => img.role === 'background') || null;
      const styleImages = images.filter((img) => img.role === 'style');

      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch('/api/custom-characters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: characterName || (language === 'he' ? 'דמות מותאמת' : 'Custom Character'),
          name_he: characterName || 'דמות מותאמת',
          name_en: characterName || 'Custom Character',
          prompt: customPrompt,
          description: customPrompt.slice(0, 200),
          background_image: backgroundImage?.dataUrl || null,
          style_images: styleImages.map((img) => img.dataUrl),
          category: 'custom',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      toast.success(
        language === 'he'
          ? 'הדמות נשמרה בהצלחה ותוכל לבחור אותה באירועים'
          : 'Character saved and can be used in events'
      );

      setCharacterName('');
      setCustomPrompt('');
      setImages([]);
    } catch (err) {
      console.error('Failed to save custom character', err);
      toast.error(
        language === 'he'
          ? `שמירת הדמות נכשלה: ${err.message}`
          : `Failed to save character: ${err.message}`
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
            <Wand2 className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">
              {language === 'he' ? 'יצירה מותאמת אישית' : 'Custom Creation'}
            </span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'he' ? 'צור דמות מותאמת אישית' : 'Create Custom Character'}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {language === 'he'
              ? 'תאר את הדמות שאתה רוצה להפוך אליה, והבינה המלאכותית תיצור אותה עבורך'
              : 'Describe the character you want to transform into, and AI will create it for you'}
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-2xl p-8"
        >
          {/* Character Name */}
          <div className="mb-6">
            <Label className="text-white mb-2 block">
              {language === 'he' ? 'שם הדמות (אופציונלי)' : 'Character Name (optional)'}
            </Label>
            <Input
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              placeholder={language === 'he' ? 'למשל: גיבור הגלקסיה' : 'e.g., Galaxy Hero'}
              className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-12"
            />
          </div>

          {/* Prompt */}
          <div className="mb-6">
            <Label className="text-white mb-2 block">
              {language === 'he' ? 'תיאור הדמות' : 'Character Description'}
            </Label>
            <Textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder={language === 'he' 
                ? 'תאר את הדמות בפירוט: בגדים, סגנון, רקע, אווירה...'
                : 'Describe the character in detail: clothing, style, background, atmosphere...'}
              className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 min-h-[150px] resize-none"
            />
          </div>

          {/* Images upload / background selection */}
          <div className="mb-6">
            <Label className="text-white mb-2 block">
              {language === 'he'
                ? 'תמונות לדמות (רקע והשראה)'
                : 'Images for this character (background & style)'}
            </Label>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesChange}
                className="text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-violet-600 file:text-white hover:file:bg-violet-700"
              />
              <p className="text-xs text-slate-500 max-w-sm">
                {language === 'he'
                  ? 'ניתן להעלות מספר תמונות. אחת מהן יכולה להיות הרקע הקבוע לדמות באירוע.'
                  : 'You can upload several images. One of them can be the fixed background for this character.'}
              </p>
            </div>

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-72 overflow-y-auto custom-scrollbar pe-1">
                {images.map((img) => (
                  <div
                    key={img.id}
                    className="relative rounded-xl overflow-hidden border border-slate-700 bg-slate-900/60"
                  >
                    <img
                      src={img.dataUrl}
                      alt={img.name}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute top-2 left-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAsBackground(img.id)}
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border backdrop-blur-sm ${
                          img.role === 'background'
                            ? 'bg-emerald-500/80 border-emerald-300 text-white'
                            : 'bg-black/60 border-slate-600 text-slate-200'
                        }`}
                      >
                        <Star className="w-3 h-3" />
                        {language === 'he' ? 'רקע' : 'Background'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-slate-200 flex items-center justify-center hover:bg-red-600/80 hover:text-white"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <div className="px-2 py-1 border-t border-slate-700 flex items-center justify-between text-[11px] text-slate-300">
                      <span className="truncate max-w-[70%]" title={img.name}>
                        {img.name}
                      </span>
                      <span className="uppercase text-[10px] text-slate-500">
                        {img.role === 'background'
                          ? language === 'he' ? 'רקע' : 'BG'
                          : 'STYLE'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-violet-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-white mb-1">
                  {language === 'he' ? 'טיפים לתוצאות טובות:' : 'Tips for best results:'}
                </h4>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>{language === 'he' ? '• תאר את הבגדים והאביזרים בפירוט' : '• Describe clothing and accessories in detail'}</li>
                  <li>{language === 'he' ? '• ציין את סגנון התמונה (קומיקס, מציאותי, וכו\')' : '• Specify the image style (comic, realistic, etc.)'}</li>
                  <li>{language === 'he' ? '• הוסף פרטים על הרקע והתאורה' : '• Add details about the background and lighting'}</li>
                  <li>{language === 'he' ? '• וודא שהפנים גלויות (ללא מסכות)' : '• Ensure face is visible (no masks)'}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Examples */}
          <div className="mb-8">
            <Label className="text-white mb-3 block">
              {language === 'he' ? 'דוגמאות להשראה:' : 'Examples for inspiration:'}
            </Label>
            <div className="flex flex-wrap gap-2">
              {examples.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => handleExample(example)}
                  className="text-sm px-3 py-2 rounded-lg bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border border-slate-700"
                >
                  {example.substring(0, 40)}...
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              onClick={handleSaveCharacter}
              disabled={isSaving || !customPrompt.trim()}
              className="w-full sm:w-auto h-12 text-sm bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              {language === 'he' ? 'שמור דמות לשימוש באירועים' : 'Save character for events'}
            </Button>

            <Link
              to={`${createPageUrl('Photobooth')}?customPrompt=${encodeURIComponent(customPrompt)}&customName=${encodeURIComponent(characterName)}`}
              className="w-full sm:w-auto"
            >
              <Button
                disabled={!customPrompt.trim()}
                className="w-full h-12 text-sm bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Camera className="w-5 h-5" />
                {language === 'he' ? 'המשך לצילום' : 'Continue to Camera'}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-1 rotate-180' : 'ml-1'}`} />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <Link to={createPageUrl('Gallery')} className="text-slate-400 hover:text-white transition-colors">
            {language === 'he' ? '← חזרה לגלריה' : '← Back to Gallery'}
          </Link>
        </div>
      </div>
    </div>
  );
}
